<script setup lang="ts">
import * as R from 'ramda'
import { computed, ComputedRef } from 'vue'

import { Button, Tooltip } from '@collector/shared-ui'
import { IncidentId } from '@collector/sportsapi-client-legacy'
import SportIncidentIcon from '@desktop/views/Relation/components/RelationView/SportIncidentIcon.vue'
import { useIncidentName } from '@desktop/views/Relation/Sports/composables'

import { useAddIncidentConfig } from '../../composables'

type ButtonProps = InstanceType<typeof Button>['$props']

interface Props {
  incidentId: IncidentId
  size?: 'sm' /** 32px */ | 'md' /** 40px */ | 'lg' /** 48px */
  disabled?: boolean
  width?: ButtonProps['width']
  selected?: boolean
  withIcon?: 'left' | 'top'
  tooltip?: string
  variant?: ButtonProps['variant']
}
const props = defineProps<Props>()

type Emits = {
  click: [incidentId: IncidentId]
}
defineEmits<Emits>()

// composables
const { getIncidentName } = useIncidentName()
const {
  isScoreIncident,
  isAttemptIncident,
  isStoppedIncident,
  isMissedIncident,
  isHighlightedIncident,
} = useAddIncidentConfig()

// computed
const incidentName = computed(() => getIncidentName(props.incidentId))

const isDefault = computed(
  () =>
    !props.variant &&
    !R.any(
      (fn) => fn(props.incidentId),
      [
        isScoreIncident,
        isAttemptIncident,
        isStoppedIncident,
        isMissedIncident,
        isHighlightedIncident,
      ],
    ),
)

const variant: ComputedRef<ButtonProps['variant']> = computed(() => {
  if (props.variant) return props.variant

  if (isDefault.value) return 'default'
  if (isScoreIncident(props.incidentId)) return 'score'
  if (isAttemptIncident(props.incidentId)) return 'attempt'
  if (isStoppedIncident(props.incidentId)) return 'stopped'
  if (isMissedIncident(props.incidentId)) return 'missed'
  if (isHighlightedIncident(props.incidentId)) return 'highlighted'

  return undefined
})
</script>

<template>
  <Button
    class="text-neutral-dark-2 disabled:text-neutral-dark-2/30 rounded-sm text-xs leading-3.5 font-bold"
    :variant
    dir="ltr"
    :disabled
    :selected
    :size
    :width
    @click="$emit('click', incidentId)"
  >
    <Tooltip class="h-full">
      <template #default>
        <div
          class="inline-flex h-full w-full items-center justify-center"
          :class="{
            'gap-x-1': withIcon,
            'flex-col gap-y-0.5': withIcon === 'top',
          }"
        >
          <SportIncidentIcon
            v-if="withIcon"
            :class="{
              'opacity-30': disabled,
            }"
            :incidentId
            size="3xs"
          />
          <slot v-bind="{ incidentName }">
            {{ incidentName }}
          </slot>
        </div>
      </template>
      <template
        v-if="tooltip"
        #tooltip
      >
        <div class="font-bold">{{ tooltip }}</div>
      </template>
    </Tooltip>
  </Button>
</template>
